/*
 * Copyright (C) 2026 to Present Applause App Quality, Inc. All rights reserved.
 */

import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, Inject, Input, OnInit, Output,
} from '@angular/core';
import { FeedStoreService } from '../../services/feed-store.service';

type FeedType = 'followedOnly' | 'entireCommunity';

// The community feed: a "followed only" / "entire community" switch over two paged lists.
@Component({
  selector: 'ut-community-feed',
  templateUrl: './community-feed.component.html',
  styleUrls: ['./community-feed.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CommunityFeedComponent implements OnInit {
  @Input({ required: true }) user: any;
  @Output() itemCommented = new EventEmitter<any>();

  feedType: FeedType = 'followedOnly';
  stores: Record<FeedType, any>;

  constructor(
    @Inject('HomeFeedService') private HomeFeedService: any,
    private feedStore: FeedStoreService,
    private cdr: ChangeDetectorRef,
    private el: ElementRef<HTMLElement>,
  ) {}

  ngOnInit(): void {
    this.stores = {
      followedOnly: this.feedStore.create((params) => this.HomeFeedService.personalFeed(params)),
      entireCommunity: this.feedStore.create((params) => this.HomeFeedService.feed(params), { sort: '-published_at' }),
    };
    this.track(this.stores.followedOnly.loadOnce());
    this.track(this.stores.entireCommunity.loadOnce());
  }

  get store(): any {
    return this.stores[this.feedType];
  }

  // Show a newly posted status at the top of both lists.
  prepend(status: any): void {
    this.stores.followedOnly.feedItems.unshift(status);
    this.stores.entireCommunity.feedItems.unshift(status);
    this.cdr.markForCheck();
  }

  // The deleted card took focus with it (its ⋯ menu), so focus the card that takes its place.
  remove(item: any): void {
    const index = this.store.feedItems.findIndex((i) => i.id === item.id && i.type === item.type);
    Object.values(this.stores).forEach((store) => {
      store.feedItems = store.feedItems.filter((i) => !(i.id === item.id && i.type === item.type));
    });
    this.cdr.markForCheck();
    setTimeout(() => {
      const cards = this.el.nativeElement.querySelectorAll<HTMLElement>('.feed-item');
      cards[Math.min(index, cards.length - 1)]?.focus();
    });
  }

  // The button disappears while loading, so focus moves to the first new post once it lands.
  loadMore(): void {
    if (this.store.isLoading || this.store.isLastPage) return;
    const firstNew = this.store.feedItems.length;
    this.track(this.store.loadMore().then(() => {
      setTimeout(() => this.el.nativeElement.querySelectorAll<HTMLElement>('.feed-item')[firstNew]?.focus());
    }));
  }

  // FeedStore mutates in place, so re-render when a page starts and when it lands.
  private track(promise: Promise<unknown>): void {
    this.cdr.markForCheck();
    promise.then(() => this.cdr.markForCheck());
  }
}
